import { Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import styled from "styled-components";

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  link: string;
  createdOn: Date;
}

interface JobItemCardProps {
  data: Job;
  index: number;
}

const JobItemCard = memo(({ data, index }: JobItemCardProps) => {
  return (
    <div key={index} style={{ marginTop: "24px" }}>
      <Stack
        direction={"column"}
        spacing={"20px"}
        style={{ width: "100%", alignItems: "flex-start" }}
      >
        <Typography
          sx={{
            color: "#002376",
            fontFamily: "Switzer",
            fontSize: { xs: "22px", sm: "24px" },
            fontStyle: "normal",
            fontWeight: "700",
            lineHeight: "normal"
          }}
        >
          {data.title}
        </Typography>

        <Divider
          sx={{
            color: "#CCCECF",
            opacity: 1,
            width: "100%"
          }}
        />

        <Stack direction={"column"} spacing={"10px"}>
          <Typography
            sx={{
              color: "#0074FF",
              fontFamily: "Switzer",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal"
            }}
          >
            DEPARTMENT
          </Typography>

          <Typography
            sx={{
              color: "#3A5B98",
              fontFamily: "Switzer",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal"
            }}
          >
            {data.department}
          </Typography>
        </Stack>

        <Divider
          sx={{
            color: "#CCCECF",
            opacity: 1,
            width: "100%"
          }}
        />

        <Stack direction={"column"} spacing={"10px"}>
          <Typography
            sx={{
              color: "#0074FF",
              fontFamily: "Switzer",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal"
            }}
          >
            LOCATION
          </Typography>

          <Typography
            sx={{
              color: "#3A5B98",
              fontFamily: "Switzer",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal"
            }}
          >
            {data.location}
          </Typography>
        </Stack>

        <Divider
          sx={{
            color: "#CCCECF",
            opacity: 1,
            width: "100%"
          }}
        />

        <ResponsiveLink
          href={data.link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ width: "100%" }}
        >
          <Button
            sx={{
              backgroundColor: "#002376",
              color: "#FFF",
              fontFamily: "Switzer",
              fontSize: "16px",
              width: { xs: "100%", sm: "fit-content" },
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal",
              textTransform: "none",
              padding: { xs: "10px 40px", sm: "8px 28px" },
              borderRadius: "16px",
              border: "2.5px solid #837BF5",
              gap: "12px"
            }}
          >
            Apply
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="12"
              viewBox="0 0 9 15"
              fill="none"
            >
              <path
                d="M1.37891 1.25806L7.47292 7.35205L1.37891 13.4451"
                stroke="white"
                stroke-width="2"
              />
            </svg>
          </Button>
        </ResponsiveLink>
      </Stack>
    </div>
  );
});

const MainCareersSection = () => {
  const [jobList, setJobList] = useState<Job[]>([]);

  useEffect(() => {
    handleInitializeJobList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInitializeJobList = () => {
    let arrayData: Job[] = [
      {
        id: 1,
        title: "Kotlin Backend Engineer",
        department: "Engineering",
        location: "Available in Jakarta or Surabaya",
        createdOn: new Date("17/06/2025"),
        link: "https://id.jobstreet.com/id/job/84813073?ref=search-standalone&type=standard&origin=jobTitle#sol=76ebae660c448d2841e4084e9099d71c44696e4a"
      }
    ];

    let tempArray: Job[] = [];
    arrayData.forEach((element) => {
      const createdDate = new Date(element.createdOn);
      const expirationDate = new Date(createdDate);
      expirationDate.setDate(createdDate.getDate() + 30);
      if (new Date(element.createdOn) < expirationDate) {
        tempArray.push(element);
      }
    });

    setJobList(tempArray);
  };

  return (
    <section
      id={"careers-section"}
      style={{
        width: "100%",
        display: "flex",
        position: "relative",
        marginTop: "125px"
      }}
    >
      <Stack
        direction={"column"}
        spacing={{ xs: "50px", sm: "75px" }}
        sx={{ width: "100%", padding: { xs: "48px 24px", sm: "60px 48px" } }}
      >
        <div
          id={"main-sub-section"}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Stack
            direction={"column"}
            spacing={"12px"}
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <Typography
              sx={{
                color: "#0074FF",
                fontFamily: "Switzer",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
                textAlign: "center"
              }}
            >
              CAREERS
            </Typography>

            <Typography
              sx={{
                color: "#002376",
                fontFamily: "Switzer",
                fontSize: "42px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
                textAlign: "center"
              }}
            >
              Be a Part of Majura
            </Typography>
          </Stack>
        </div>

        <div
          id={"search-sub-section"}
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <Stack></Stack>

          <Typography
            sx={{
              color: "#424143",
              fontFamily: "Switzer",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal",
              textAlign: "center"
            }}
          >
            {jobList.length} roles across all departments and all locations
          </Typography>
        </div>

        <div
          id={"job-listing-sub-section"}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: jobList.length === 0 ? "center" : "flex-start"
          }}
        >
          {jobList.length > 0 ? (
            <Grid
              container
              spacing={{ xs: "64px", md: "32px" }}
              style={{ width: "100%" }}
            >
              {jobList.map((job, index) => (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4 }}
                  sx={{ padding: { xs: "0 32px", md: "0 32px", xl: "0 64px" } }}
                >
                  <JobItemCard data={job} index={index} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              sx={{
                color: "#002376",
                fontFamily: "Switzer",
                fontSize: "41.9px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal"
              }}
            >
              No job listings available at the moment.
            </Typography>
          )}
        </div>
      </Stack>
    </section>
  );
};

const ResponsiveLink = styled.a`
  width: fit-content;

  display: inline-block;
  text-decoration: none;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

export default MainCareersSection;
